'use client';

import React, { useEffect, useState } from 'react';

import Button from 'molecules/Button';
import Icon from 'molecules/Icon';

import styles from 'components/ListingHeader/pagination.module.scss';

interface PaginationProps {
  totalItems: number;
  limit: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const SearchPagination: React.FC<PaginationProps> = ({ totalItems, limit, currentPage, onPageChange }) => {
  const [totalPages, setTotalPages] = useState<number>(0);

  useEffect(() => {
    setTotalPages(Math.ceil(totalItems / limit));
  }, [totalItems, limit]);

  const handlePageClick = (page: number) => {
    onPageChange(page);
  };

  const getPageNumbers = () => {
    const pageNumbersToShow = 5;
    const pages: (number | string)[] = [];

    if (totalPages <= pageNumbersToShow + 2) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    pages.push(1);

    if (currentPage <= 3) {
      pages.push(2, 3, 4, 5, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push('...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }

    return pages;
  };

  return (
    <div className={styles.pagination}>
      <Button
        className={styles.prevLink}
        direction="right"
        tabIndex={0}
        onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
        disabled={currentPage === 1}
      >
        <Icon size={19} icon="arrow-right" />
        Previous
      </Button>

      <div className={styles.paginationNumberWrapper}>
        {getPageNumbers().map((page, index) =>
          page === '...' ? (
            <span key={`ellipsis-${index}`} className={styles.ellipsis}>
              ...
            </span>
          ) : (
            <button
              className={`${styles.paginationNumber} ${currentPage === page ? styles.active : ''}`}
              key={page}
              type="button"
              data-qa="option"
              data-key={page}
              tabIndex={0}
              onClick={() => handlePageClick(page as number)}
            >
              <div data-qa="label">{page}</div>
            </button>
          ),
        )}
      </div>

      <Button
        className={styles.arrowLink}
        direction="right"
        iconId="arrow-right"
        tabIndex={0}
        onClick={() => handlePageClick(Math.min(currentPage + 1, totalPages))}
        disabled={currentPage === totalPages}
      >
        Next
      </Button>
    </div>
  );
};

export default SearchPagination;
